import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { s, authHeaders, Toast, standardGridSx } from './listPageStyles';
import { usePageActions } from '../contexts/PermissionContext';
import { RiskCategoriesPage } from './RiskCategoriesPage';
import { RiskMastersPage } from './RiskMastersPage';

const riskDefinitionTabs = [
  { key: 'tracking', label: 'Risk Izleme' },
  { key: 'definitions', label: 'Risk Tanimi' },
  { key: 'categories', label: 'Risk Kategorileri' },
  { key: 'masters', label: 'Risk Master Tanim' },
];

const riskDefinitionStyles = {
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  tab: {
    minHeight: 36,
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-secondary, #4b5563)',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
  },
  tabActive: {
    background: 'var(--accent)',
    borderColor: 'var(--accent)',
    color: '#fff',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    marginBottom: 16,
  },
  summaryCard: {
    padding: '14px 16px',
    borderRadius: 10,
    border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-card, #ffffff)',
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)',
    cursor: 'default',
  },
  summaryCardInteractive: {
    cursor: 'pointer',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
  },
  summaryCardActive: {
    borderColor: 'var(--accent)',
    boxShadow: '0 10px 24px rgba(0, 115, 234, 0.16)',
    transform: 'translateY(-1px)',
  },
  summaryLabel: {
    margin: 0,
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--text-secondary, #4b5563)',
  },
  summaryValue: {
    margin: '6px 0 0',
    fontSize: 26,
    fontWeight: 800,
    color: 'var(--text-primary, #111827)',
  },
};

function toDateInput(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function getRowValue(row) {
  if (row.dataType === 1) return row.moneyValue ?? '';
  if (row.dataType === 2) return row.numberValue ?? '';
  if (row.dataType === 3) return toDateInput(row.dateValue);
  if (row.dataType === 4) return row.percentageValue ?? '';
  if (row.dataType === 5) return row.boolValue === null || row.boolValue === undefined ? '' : String(row.boolValue);
  return '';
}

function toDecimal(value) {
  if (value === '' || value === null || value === undefined) return null;
  const normalized = String(value).replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatRiskValue(row, value = row.value) {
  if (value === '' || value === null || value === undefined) return '-';
  if (row.dataType === 1) {
    const parsed = toDecimal(value);
    return parsed === null ? '-' : parsed.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (row.dataType === 2) {
    const parsed = toDecimal(value);
    return parsed === null ? '-' : parsed.toLocaleString('tr-TR', { maximumFractionDigits: 4 });
  }
  if (row.dataType === 3) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
  }
  if (row.dataType === 4) {
    const parsed = toDecimal(value);
    return parsed === null ? '-' : `%${parsed.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (row.dataType === 5) {
    return value === true || value === 'true' ? 'Evet' : value === false || value === 'false' ? 'Hayir' : '-';
  }
  return String(value);
}

function hashString(value) {
  let hash = 2166136261;
  const text = String(value || '');
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function seededUnit(seed, shift = 0) {
  const raw = Math.sin(seed + shift * 17.37) * 10000;
  return raw - Math.floor(raw);
}

function roundValue(value, digits = 2) {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function getRiskPalette(percent) {
  if (percent > 80) {
    return { statusColor: '#16a34a', trackColor: '#dcfce7', textColor: '#166534' };
  }
  if (percent >= 50) {
    return { statusColor: '#d97706', trackColor: '#fef3c7', textColor: '#92400e' };
  }
  return { statusColor: '#dc2626', trackColor: '#fee2e2', textColor: '#991b1b' };
}

function getDemoRiskStatus(index) {
  if (index < 4) return 'red';
  if (index < 13) return 'yellow';
  return 'green';
}

function getShuffledStatusMap(sourceRows) {
  const detailRows = sourceRows.map((row, index) => ({
    riskMasterId: row.riskMasterId,
    sortKey: hashString(`${row.riskMasterId}-${row.masterTextId || row.name}-${index}`),
  }));

  const ordered = [...detailRows].sort((left, right) => left.sortKey - right.sortKey);
  const map = new Map();

  ordered.forEach((item, index) => {
    map.set(item.riskMasterId, getDemoRiskStatus(index));
  });

  return map;
}

function isActualBelowDefined(row, actualValue) {
  if (row.dataType === 1 || row.dataType === 2 || row.dataType === 4) {
    const definedValue = toDecimal(row.value);
    const actualNumeric = toDecimal(actualValue);
    if (definedValue === null || actualNumeric === null) return false;
    return actualNumeric < definedValue;
  }

  if (row.dataType === 3) {
    const definedDate = row.value ? new Date(row.value) : null;
    const actualDate = actualValue ? new Date(actualValue) : null;
    if (!definedDate || Number.isNaN(definedDate.getTime()) || !actualDate || Number.isNaN(actualDate.getTime())) return false;
    return actualDate < definedDate;
  }

  return false;
}

function deriveActualValue(row, targetStatus) {
  const seed = hashString(row.riskMasterId || row.masterTextId || row.name);
  const redRatio = 1.95 + (seededUnit(seed, 11) * 1.25);
  const yellowRatio = 1.22 + (seededUnit(seed, 12) * 0.48);
  const greenRatio = 0.58 + (seededUnit(seed, 13) * 0.28);
  const redPercent = 22 + Math.round(seededUnit(seed, 21) * 25);
  const yellowPercent = 52 + Math.round(seededUnit(seed, 22) * 26);
  const greenPercent = 81 + Math.round(seededUnit(seed, 23) * 15);

  if (row.dataType === 1 || row.dataType === 2 || row.dataType === 4) {
    const definedValue = toDecimal(row.value);
    const baseValue = definedValue ?? Math.max(10, roundValue((seededUnit(seed, 1) * 900) + 50, row.dataType === 2 ? 0 : 2));
    const ratio = targetStatus === 'red' ? redRatio : targetStatus === 'yellow' ? yellowRatio : greenRatio;
    const actualValue = roundValue(baseValue * ratio, row.dataType === 2 ? 0 : 2);
    const matchPercent = targetStatus === 'red' ? redPercent : targetStatus === 'yellow' ? yellowPercent : greenPercent;
    return { actualValue, matchPercent };
  }

  if (row.dataType === 3) {
    const definedValue = row.value ? new Date(row.value) : null;
    const baseDate = definedValue && !Number.isNaN(definedValue.getTime())
      ? definedValue
      : addDays(new Date(), Math.round(seededUnit(seed, 3) * 20));
    const offset = targetStatus === 'red'
      ? 14 + Math.round(seededUnit(seed, 31) * 12)
      : targetStatus === 'yellow'
        ? 7 + Math.round(seededUnit(seed, 32) * 6)
        : -1 - Math.round(seededUnit(seed, 33) * 8);
    const actualDate = addDays(baseDate, offset);
    const matchPercent = targetStatus === 'red' ? redPercent : targetStatus === 'yellow' ? yellowPercent : greenPercent;
    return { actualValue: actualDate.toISOString(), matchPercent };
  }

  if (row.dataType === 5) {
    const definedValue = row.value === 'true' ? true : row.value === 'false' ? false : null;
    const baseValue = definedValue ?? (seededUnit(seed, 5) >= 0.5);
    const actualValue = targetStatus === 'green' ? baseValue : !baseValue;
    const matchPercent = targetStatus === 'red' ? redPercent : targetStatus === 'yellow' ? yellowPercent : 100;
    return { actualValue: String(actualValue), matchPercent };
  }

  return { actualValue: '', matchPercent: 0 };
}

function buildGroupedRows(sourceRows, expanded, mapChild) {
  const groups = new Map();

  sourceRows.forEach((row) => {
    const categoryId = row.categoryId;
    if (!groups.has(categoryId)) {
      groups.set(categoryId, {
        id: `category-${categoryId}`,
        categoryId,
        isGroup: true,
        categoryDescription: row.categoryDescription || row.categoryTextId || 'Kategori',
        children: [],
      });
    }

    groups.get(categoryId).children.push(mapChild(row));
  });

  const result = [];
  groups.forEach((group) => {
    result.push(group);
    if (expanded[group.categoryId] !== false) {
      result.push(...group.children);
    }
  });

  return result;
}

export function LinkRiskTrackingPage() {
  // Menü aksiyon hakkı: risk tanımları var olan master kayıtların değerlerini günceller → Güncelleme hakkı.
  // Yenile/filtre/sekme ve ızgaradaki değer girişleri (Kaydet'e kadar yerel) kapılanmaz.
  const { canEdit } = usePageActions();
  const [activeDefinition, setActiveDefinition] = useState('tracking');
  const [sourceRows, setSourceRows] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [trackingFilter, setTrackingFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/riskdefinitions/Editor', { headers: authHeaders() });
      if (!res.ok) throw new Error('Risk tanimlari alinamadi');
      const data = await res.json();
      const mapped = data.map((row) => {
        const value = getRowValue(row);
        return { ...row, value, originalValue: String(value ?? '') };
      });
      setSourceRows(mapped);
      setExpanded((current) => {
        const next = { ...current };
        mapped.forEach((row) => {
          if (next[row.categoryId] === undefined) next[row.categoryId] = true;
        });
        return next;
      });
    } catch (error) {
      showToast('error', error.message || 'Veri alinirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const definitionRows = useMemo(
    () => buildGroupedRows(sourceRows, expanded, (row) => ({ ...row, id: row.riskMasterId })),
    [sourceRows, expanded],
  );

  const trackingRows = useMemo(
    () => {
      const statusMap = getShuffledStatusMap(sourceRows);
      return buildGroupedRows(sourceRows, expanded, (row) => {
        const targetStatus = statusMap.get(row.riskMasterId) || 'green';
      const actual = deriveActualValue(row, targetStatus);
      const forceGreen = isActualBelowDefined(row, actual.actualValue);
      return {
        ...row,
        id: `tracking-${row.riskMasterId}`,
        actualValue: actual.actualValue,
        matchPercent: roundValue(actual.matchPercent, 0),
        riskStatus: forceGreen ? 'green' : targetStatus,
      };
      });
    },
    [sourceRows, expanded],
  );

  const filteredTrackingRows = useMemo(() => {
    if (trackingFilter === 'all') return trackingRows;

    const matcher = (row) => {
      if (trackingFilter === 'green') return row.riskStatus === 'green';
      if (trackingFilter === 'yellow') return row.riskStatus === 'yellow';
      if (trackingFilter === 'red') return row.riskStatus === 'red';
      return true;
    };

    const result = [];
    let activeGroup = null;
    let groupHasVisibleChild = false;

    trackingRows.forEach((row) => {
      if (row.isGroup) {
        if (activeGroup && groupHasVisibleChild) {
          result.push(activeGroup);
        }
        activeGroup = row;
        groupHasVisibleChild = false;
        return;
      }

      if (matcher(row)) {
        if (activeGroup && !groupHasVisibleChild) {
          result.push(activeGroup);
          groupHasVisibleChild = true;
        }
        result.push(row);
      }
    });

    return result;
  }, [trackingRows, trackingFilter]);

  const summaryCards = useMemo(() => {
    const detailRows = trackingRows.filter((row) => !row.isGroup);
    const greenCount = detailRows.filter((row) => row.riskStatus === 'green').length;
    const yellowCount = detailRows.filter((row) => row.riskStatus === 'yellow').length;
    const redCount = detailRows.filter((row) => row.riskStatus === 'red').length;
    const averageMatch = detailRows.length > 0
      ? roundValue(detailRows.reduce((total, row) => total + row.matchPercent, 0) / detailRows.length, 0)
      : 0;

    return [
      { key: 'all', label: 'Toplam Risk', value: detailRows.length },
      { label: 'Uyum Ortalamasi', value: `%${averageMatch}` },
      { key: 'green', label: 'Yesil Durum', value: greenCount },
      { key: 'yellow', label: 'Sari Durum', value: yellowCount },
      { key: 'red', label: 'Kirmizi Durum', value: redCount },
    ];
  }, [trackingRows]);

  const updateValue = (riskMasterId, value) => {
    setSourceRows((current) => current.map((row) => (row.riskMasterId === riskMasterId ? { ...row, value } : row)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const items = sourceRows.map((row) => ({
        riskMasterId: row.riskMasterId,
        moneyValue: row.dataType === 1 ? toDecimal(row.value) : null,
        numberValue: row.dataType === 2 ? toDecimal(row.value) : null,
        dateValue: row.dataType === 3 && row.value ? new Date(row.value).toISOString() : null,
        percentageValue: row.dataType === 4 ? toDecimal(row.value) : null,
        boolValue: row.dataType === 5 && row.value !== '' ? row.value === 'true' : null,
      }));
      const res = await fetch('/api/riskdefinitions/Save', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('Risk degerleri kaydedilemedi');
      showToast('success', 'Risk tanimlari kaydedildi');
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Kayit sirasinda hata olustu');
    } finally {
      setSaving(false);
    }
  };

  const renderDefinitionValueInput = (params) => {
    const row = params.row;
    if (row.isGroup) return null;
    const isDirty = String(row.value ?? '') !== String(row.originalValue ?? '');
    const isEditing = editingRowId === row.riskMasterId;
    const inputStyle = {
      width: '100%',
      height: 32,
      border: '1px solid var(--border-color, #d1d5db)',
      borderRadius: 6,
      padding: '0 8px',
      background: 'var(--input-bg, #fff)',
      color: 'var(--text-primary, #111827)',
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
      fontWeight: isDirty ? 800 : 600,
    };
    const common = {
      style: inputStyle,
      value: row.value ?? '',
      onClick: (event) => event.stopPropagation(),
      onChange: (event) => updateValue(row.riskMasterId, event.target.value),
      onFocus: () => setEditingRowId(row.riskMasterId),
      onBlur: () => setEditingRowId(null),
    };

    if (row.dataType === 3) {
      return (
        <input
          {...common}
          type={isEditing ? 'date' : 'text'}
          readOnly={!isEditing}
          value={isEditing ? (row.value ?? '') : formatRiskValue(row)}
        />
      );
    }

    if (row.dataType === 5) {
      return (
        <select {...common}>
          <option value="">Seciniz</option>
          <option value="true">Evet</option>
          <option value="false">Hayir</option>
        </select>
      );
    }

    return (
      <input
        {...common}
        type="text"
        inputMode="decimal"
        value={isEditing ? (row.value ?? '') : formatRiskValue(row)}
      />
    );
  };

  const isRowDirty = (row) => !row.isGroup && String(row.value ?? '') !== String(row.originalValue ?? '');

  const isGroupDirty = (categoryId) => sourceRows.some((row) => row.categoryId === categoryId && isRowDirty(row));

  const renderGroupCell = (params, dirtyIndicator) => {
    if (!params.row.isGroup) return null;
    const isOpen = expanded[params.row.categoryId] !== false;
    return (
      <button
        type="button"
        style={{ border: 'none', background: 'transparent', color: 'var(--text-primary, #111827)', fontWeight: 800, cursor: 'pointer', padding: 0 }}
        onClick={() => setExpanded((current) => ({ ...current, [params.row.categoryId]: !isOpen }))}
      >
        {isOpen ? 'v' : '>'} {params.row.categoryDescription}{dirtyIndicator ? ' *' : ''}
      </button>
    );
  };

  const definitionColumns = [
    {
      field: 'name',
      headerName: 'Risk Tanimi',
      flex: 1.1,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isGroup) return renderGroupCell(params, isGroupDirty(params.row.categoryId));
        return <span style={{ fontWeight: 600 }}>{params.row.name}</span>;
      },
    },
    { field: 'description', headerName: 'Aciklama', flex: 1.3, minWidth: 260, sortable: false, renderCell: (params) => (params.row.isGroup ? null : params.row.description) },
    { field: 'dataTypeName', headerName: 'Veri Tipi', width: 140, sortable: false, renderCell: (params) => (params.row.isGroup ? null : params.row.dataTypeName) },
    { field: 'value', headerName: 'Tanimlanan Deger', width: 220, sortable: false, renderCell: renderDefinitionValueInput },
  ];

  const trackingColumns = [
    {
      field: 'name',
      headerName: 'Risk Tanimi',
      flex: 1.1,
      minWidth: 240,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isGroup) return renderGroupCell(params, false);
        return <span style={{ fontWeight: 700 }}>{params.row.name}</span>;
      },
    },
    {
      field: 'description',
      headerName: 'Aciklama',
      flex: 1.2,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => (params.row.isGroup ? null : params.row.description || '-'),
    },
    {
      field: 'value',
      headerName: 'Tanimlanan Deger',
      width: 210,
      sortable: false,
      renderCell: (params) => (params.row.isGroup ? null : (
        <div style={{ width: '100%', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {formatRiskValue(params.row, params.row.value)}
        </div>
      )),
    },
    {
      field: 'actualValue',
      headerName: 'Actual Deger',
      width: 220,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isGroup) return null;
        return (
          <div style={{ width: '100%', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {formatRiskValue(params.row, params.row.actualValue)}
          </div>
        );
      },
    },
    {
      field: 'matchPercent',
      headerName: 'Uyum',
      width: 220,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isGroup) return null;
        const palette = getRiskPalette(params.row.riskStatus === 'green' ? 100 : params.row.riskStatus === 'yellow' ? 60 : 0);
        return (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                height: 28,
                padding: '0 8px',
                border: 'none',
                borderRadius: 9999,
                background: palette.trackColor,
                overflow: 'hidden',
                boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.28)',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: `${params.row.matchPercent}%`,
                  borderRadius: 9999,
                  background: `linear-gradient(90deg, ${palette.statusColor} 0%, ${palette.statusColor}CC 100%)`,
                  transition: 'width 0.2s ease',
                }}
              />
              <span
                style={{
                  position: 'relative',
                  zIndex: 1,
                  color: palette.statusColor,
                  fontSize: 11,
                  fontWeight: 800,
                  background: 'rgba(255,255,255,0.88)',
                  border: '1px solid rgba(255,255,255,0.92)',
                  borderRadius: 9999,
                  padding: '1px 6px',
                  lineHeight: 1.2,
                }}
              >
                %{params.row.matchPercent}
              </span>
            </div>
          </div>
        );
      },
    },
  ];

  const renderDefinitionTabs = () => (
    <div style={riskDefinitionStyles.tabBar}>
      {riskDefinitionTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          style={{
            ...riskDefinitionStyles.tab,
            ...(activeDefinition === tab.key ? riskDefinitionStyles.tabActive : {}),
          }}
          onClick={() => setActiveDefinition(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (activeDefinition === 'categories') {
    return (
      <div style={s.container}>
        {renderDefinitionTabs()}
        <RiskCategoriesPage />
      </div>
    );
  }

  if (activeDefinition === 'masters') {
    return (
      <div style={s.container}>
        {renderDefinitionTabs()}
        <RiskMastersPage />
      </div>
    );
  }

  if (activeDefinition === 'definitions') {
    return (
      <div style={s.container}>
        {renderDefinitionTabs()}
        <div style={s.pageHeader}>
          <div style={s.pageHeaderText}>
            <h1 style={s.pageTitle}>Risk Tanimi</h1>
            <p style={s.pageSubtitle}>Risk tanimlarini kategori bazinda duzenleyin ve kaydedin.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={s.btnSecondary} onClick={fetchData} disabled={loading || saving}>Yenile</button>
            {canEdit && <button style={saving ? s.btnDisabled : s.btnPrimary} onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>}
          </div>
        </div>
        <div style={{ ...s.tableWrapper, borderRadius: 8 }}>
          <DataGrid
            rows={definitionRows}
            columns={definitionColumns}
            loading={loading}
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            getRowHeight={(params) => (params.model.isGroup ? 42 : 46)}
            getRowClassName={(params) => {
              if (params.row.isGroup) return isGroupDirty(params.row.categoryId) ? 'risk-category-row risk-category-row-dirty' : 'risk-category-row';
              return isRowDirty(params.row) ? 'risk-value-row-dirty' : '';
            }}
            localeText={{ noRowsLabel: 'Risk master tanimi bulunamadi' }}
            sx={{
              ...standardGridSx,
              '& .risk-category-row': {
                backgroundColor: 'rgba(43, 108, 176, 0.10) !important',
              },
              '& .risk-category-row .MuiDataGrid-cell': {
                fontWeight: 800,
                borderTop: '1px solid var(--border-color, #e5e7eb)',
              },
              '& .risk-category-row-dirty': {
                backgroundColor: 'rgba(253, 171, 61, 0.22) !important',
              },
              '& .risk-value-row-dirty': {
                backgroundColor: 'rgba(251, 191, 36, 0.28) !important',
                outline: '1px solid rgba(217, 119, 6, 0.45)',
                outlineOffset: -1,
              },
              '& .risk-value-row-dirty:hover': {
                backgroundColor: 'rgba(251, 191, 36, 0.36) !important',
              },
            }}
          />
        </div>
        <Toast toast={toast} />
      </div>
    );
  }

  return (
    <div style={s.container}>
      {renderDefinitionTabs()}
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Risk Izleme</h1>
          <p style={s.pageSubtitle}>Risk tanimlarindaki degerleri actual degerlerle karsilastirip renkli olarak izleyin.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={s.btnSecondary} onClick={fetchData} disabled={loading}>Yenile</button>
        </div>
      </div>

      <div style={riskDefinitionStyles.summaryGrid}>
        {summaryCards.map((item) => (
          <div
            key={item.label}
            style={{
              ...riskDefinitionStyles.summaryCard,
              ...(item.key ? riskDefinitionStyles.summaryCardInteractive : {}),
              ...(item.key && trackingFilter === item.key ? riskDefinitionStyles.summaryCardActive : {}),
            }}
            onClick={item.key ? () => setTrackingFilter((current) => (current === item.key ? 'all' : item.key)) : undefined}
            role={item.key ? 'button' : undefined}
            tabIndex={item.key ? 0 : undefined}
            onKeyDown={item.key ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setTrackingFilter((current) => (current === item.key ? 'all' : item.key));
              }
            } : undefined}
          >
            <p style={riskDefinitionStyles.summaryLabel}>{item.label}</p>
            <p style={riskDefinitionStyles.summaryValue}>{item.value}</p>
          </div>
        ))}
      </div>

      <div style={{ ...s.tableWrapper, borderRadius: 8 }}>
        <DataGrid
          rows={filteredTrackingRows}
          columns={trackingColumns}
          loading={loading}
          hideFooter
          disableColumnMenu
          disableRowSelectionOnClick
          getRowHeight={(params) => (params.model.isGroup ? 42 : 46)}
          getRowClassName={(params) => (params.row.isGroup ? 'risk-category-row' : '')}
          localeText={{ noRowsLabel: 'Risk tanimi bulunamadi' }}
          sx={{
            ...standardGridSx,
            '& .risk-category-row': {
              backgroundColor: 'rgba(43, 108, 176, 0.10) !important',
            },
            '& .risk-category-row .MuiDataGrid-cell': {
              fontWeight: 800,
              borderTop: '1px solid var(--border-color, #e5e7eb)',
            },
          }}
        />
      </div>
      <Toast toast={toast} />
    </div>
  );
}
